import { usePageRefreshKey } from '../../../context/PageRefreshContext';
import { localDateString } from '../../../utils/helpers/localDate';
import { useState, useEffect } from 'react';
import { Icon } from '@iconify/react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
    getAdminAttendanceDaily,
    getAdminAttendanceSummary,
    getAdminAttendancePunches,
    getAttendanceEnums,
} from '../../../redux/actions/SocietyPortal/AttendanceAction/AttendanceAction';
import { PaginationLimit } from '../../../components/PaginationLimit';
import usePermissions from '../../../hook/usePermissions';
import { PERMISSIONS } from '../../../consts/permissions';
import webTokenStorer, { selectedSocietyStorer } from '../../../utils/webStorer';
import apiEndpoints from '../../../axios/api';
import ManualPunchModal from './Component/ManualPunchModal';
import EditPunchModal from './Component/EditPunchModal';
import PunchDecisionModal from './Component/PunchDecisionModal';

const DEPARTMENTS = ['SECURITY', 'MAINTENANCE', 'HOUSEKEEPING', 'ADMINISTRATION', 'ACCOUNTS', 'MANAGEMENT', 'OTHER'];

const todayInput = () => localDateString();

const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(`${dateStr}T00:00:00`).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', weekday: 'short' });
};

const formatTime = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
};

const formatDateTime = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
};

const prettyEnum = (v) => (v || '').replace(/_/g, ' ');

const DAY_STATUS_STYLES = {
    PRESENT: 'tw-bg-emerald-50 tw-text-emerald-700',
    HALF_DAY: 'tw-bg-amber-50 tw-text-amber-700',
    INCOMPLETE: 'tw-bg-orange-50 tw-text-orange-700',
    ABSENT: 'tw-bg-gray-100 tw-text-gray-500',
    ON_LEAVE: 'tw-bg-blue-50 tw-text-blue-700',
    HOLIDAY: 'tw-bg-purple-50 tw-text-purple-700',
};

const PUNCH_STATUS_STYLES = {
    APPROVED: 'tw-bg-emerald-50 tw-text-emerald-700',
    PENDING_APPROVAL: 'tw-bg-blue-50 tw-text-blue-700',
    REJECTED: 'tw-bg-red-50 tw-text-red-700',
};

const Badge = ({ value, styles }) => (
    <span className={`tw-inline-flex tw-items-center tw-px-2 tw-py-0.5 tw-rounded tw-text-[11px] tw-font-medium ${styles[value] || 'tw-bg-gray-100 tw-text-gray-500'}`}>
        {prettyEnum(value) || '—'}
    </span>
);

// Disk-backed selfies come back as relative /uploads/... paths — resolve
// against the API origin. GCS ones are already absolute signed URLs.
const DEVICE_ICONS = { mobile: 'mdi:cellphone', tablet: 'mdi:tablet', web: 'mdi:monitor' };

// Device a punch came from: hardware / OS / browser / app version on one line,
// with device ID and IP in the tooltip. Admin-only data from the punch log API.
const DeviceInfo = ({ device, compact = false }) => {
    if (!device) return null;
    const summary = device.summary || device.device_summary
        || [device.platform, device.app_version && `App ${device.app_version}`].filter(Boolean).join(' · ');
    if (!summary && !device.device_type) return compact ? null : <span className="tw-text-[11px] tw-text-gray-300">—</span>;
    const tooltip = [
        device.device_type && `Type: ${device.device_type}`,
        device.device_brand && `Brand: ${device.device_brand}`,
        device.device_model && `Model: ${device.device_model}`,
        (device.os_version || device.platform) && `OS: ${device.os_version || device.platform}`,
        device.browser && `Browser: ${device.browser}`,
        device.app_version && `App version: ${device.app_version}`,
        device.device_id && `Device ID: ${device.device_id}`,
        device.ip_address && `IP: ${device.ip_address}`,
    ].filter(Boolean).join('\n');
    const icon = DEVICE_ICONS[device.device_type] || 'mdi:devices';
    if (compact) {
        return (
            <div className="tw-flex tw-items-center tw-gap-1 tw-text-[10px] tw-text-gray-400 tw-max-w-[160px]" title={tooltip}>
                <Icon icon={icon} className="tw-flex-shrink-0" />
                <span className="tw-truncate">{summary || prettyEnum(device.device_type)}</span>
            </div>
        );
    }
    return (
        <div className="tw-max-w-[200px]" title={tooltip}>
            <div className="tw-flex tw-items-center tw-gap-1 tw-text-gray-700">
                <Icon icon={icon} className="tw-text-sm tw-text-gray-400 tw-flex-shrink-0" />
                <span className="tw-truncate">{summary || prettyEnum(device.device_type)}</span>
            </div>
            {(device.device_id || device.ip_address) && (
                <div className="tw-text-[10px] tw-text-gray-400 tw-truncate">
                    {[device.device_id && `ID ${device.device_id}`, device.ip_address && `IP ${device.ip_address}`].filter(Boolean).join(' · ')}
                </div>
            )}
        </div>
    );
};

const resolveSelfieUrl = (url) => {
    if (!url) return null;
    if (/^https?:\/\//i.test(url)) return url;
    const base = (process.env.REACT_APP_NODE_MOBILE_BASE_URL || '').replace(/\/$/, '');
    return `${base}${url.startsWith('/') ? url : `/${url}`}`;
};

const selectClass = 'tw-w-full tw-border tw-border-gray-300 tw-rounded-md tw-px-2.5 tw-py-1.5 tw-text-xs focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-blue-500';

const ManageAttendance = () => {
    const dispatch = useDispatch();
    const { can } = usePermissions();
    const canManage = can(PERMISSIONS.ATTENDANCE_MANAGE);
    const canApprove = can(PERMISSIONS.ATTENDANCE_APPROVE);
    const canExport = can(PERMISSIONS.ATTENDANCE_EXPORT);

    const {
        adminDailyRows, adminDailyPagination, adminDailyLoading,
        adminSummaryData, adminPunches, adminPunchesPagination, adminPunchesLoading,
        attendanceEnums,
    } = useSelector(state => state.AttendanceReducer || {});

    const [tab, setTab] = useState('daily'); // daily | punches
    const [range, setRange] = useState({ from: todayInput(), to: todayInput() });
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [department, setDepartment] = useState('');
    const [dayStatus, setDayStatus] = useState('');
    const [pendingOnly, setPendingOnly] = useState(false);
    const [outOfFenceOnly, setOutOfFenceOnly] = useState(false);
    const [punchType, setPunchType] = useState('');
    const [punchStatus, setPunchStatus] = useState('');
    const [manualOnly, setManualOnly] = useState(false);
    // Drill-down from a daily row into that employee's punches for that date
    const [employeeFocus, setEmployeeFocus] = useState(null); // { id, name, date }
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(50);
    const [exporting, setExporting] = useState(false);

    const [manualOpen, setManualOpen] = useState(false);
    const [editPunch, setEditPunch] = useState(null);
    const [decision, setDecision] = useState(null); // { punch, mode }

    // Cheap manual refresh trigger after mutations
    const [refreshTick, setRefreshTick] = useState(0);
    const refreshAll = () => setRefreshTick(t => t + 1);

    useEffect(() => { dispatch(getAttendanceEnums()); }, [dispatch]);

    // Debounced search
    useEffect(() => {
        const id = setTimeout(() => { setSearch(searchInput.trim()); setPage(1); }, 400);
        return () => clearTimeout(id);
    }, [searchInput]);

    // Summary tiles for the range's end date (today by default)
    const refreshKey = usePageRefreshKey();
    useEffect(() => {
        dispatch(getAdminAttendanceSummary({ date: range.to || todayInput() }));
    }, [dispatch, range.to, refreshTick, refreshKey]);

    useEffect(() => {
        if (tab !== 'daily') return;
        dispatch(getAdminAttendanceDaily({
            search, department,
            from: range.from, to: range.to,
            day_status: dayStatus,
            has_pending_approval: pendingOnly ? true : undefined,
            out_of_fence_only: outOfFenceOnly ? true : undefined,
            page, limit,
        }));
    }, [dispatch, tab, search, department, range.from, range.to, dayStatus, pendingOnly, outOfFenceOnly, page, limit, refreshTick, refreshKey]);

    useEffect(() => {
        if (tab !== 'punches') return;
        dispatch(getAdminAttendancePunches({
            employee_id: employeeFocus?.id,
            search: employeeFocus ? undefined : search,
            department: employeeFocus ? undefined : department,
            from: employeeFocus?.date || range.from,
            to: employeeFocus?.date || range.to,
            punch_type: punchType,
            status: punchStatus,
            within_geofence: outOfFenceOnly ? false : undefined,
            is_manual: manualOnly ? true : undefined,
            page, limit,
        }));
    }, [dispatch, tab, employeeFocus, search, department, range.from, range.to, punchType, punchStatus, outOfFenceOnly, manualOnly, page, limit, refreshTick, refreshKey]);

    const switchTab = (next) => {
        setTab(next);
        setPage(1);
        setLimit(next === 'daily' ? 50 : 20);
        if (next === 'daily') setEmployeeFocus(null);
    };

    const openDayPunches = (row) => {
        setEmployeeFocus({
            id: row.employee?.employee_id,
            name: row.employee?.name || `#${row.employee?.employee_id}`,
            date: row.attendance_date,
        });
        setTab('punches');
        setPage(1);
        setLimit(20);
    };

    const openPendingQueue = () => {
        setEmployeeFocus(null);
        setPunchStatus('PENDING_APPROVAL');
        setTab('punches');
        setPage(1);
        setLimit(20);
    };

    // Export via fetch (not the axios instance) so response HEADERS stay
    // readable — the interceptor unwraps bodies, and we need
    // X-Export-Truncated to warn about partial files.
    const handleExport = async () => {
        if (exporting) return;
        setExporting(true);
        try {
            const view = tab === 'punches' ? 'punches' : 'daily';
            const params = new URLSearchParams({ view });
            const add = (k, v) => { if (v !== undefined && v !== null && v !== '') params.set(k, v); };
            add('search', employeeFocus ? '' : search);
            add('department', employeeFocus ? '' : department);
            add('employee_id', employeeFocus?.id);
            add('from', employeeFocus?.date || range.from);
            add('to', employeeFocus?.date || range.to);
            if (view === 'daily') {
                add('day_status', dayStatus);
                if (outOfFenceOnly) add('out_of_fence_only', 'true');
            } else {
                add('punch_type', punchType);
                add('status', punchStatus);
                if (outOfFenceOnly) add('within_geofence', 'false');
            }

            const base = (process.env.REACT_APP_NODE_MOBILE_BASE_URL || '').replace(/\/$/, '');
            const token = webTokenStorer.getToken()?.access_token;
            const societyId = selectedSocietyStorer.getSocietyId();
            const res = await fetch(`${base}${apiEndpoints.SOCIETY_ATTENDANCE_EXPORT_API}?${params}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    ...(societyId != null ? { 'X-Society-Id': societyId } : {}),
                },
            });
            if (!res.ok) {
                let message = 'Failed to export attendance';
                try { message = (await res.json())?.message || message; } catch { /* binary/empty body */ }
                throw new Error(message);
            }

            if (res.headers.get('X-Export-Truncated') === 'true') {
                const cap = res.headers.get('X-Export-Row-Cap');
                toast.warn(`Export hit the row cap${cap ? ` (${cap} rows)` : ''} — the file is partial. Narrow the date range.`);
            }

            const blob = await res.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `attendance-${view}-${range.from || 'all'}_to_${range.to || 'all'}.xlsx`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            toast.error(err?.message || 'Failed to export attendance');
        } finally {
            setExporting(false);
        }
    };

    const summary = adminSummaryData || {};
    const tiles = [
        { icon: 'mdi:account-group-outline', color: 'tw-bg-slate-500', label: 'Roster', value: summary.roster_count ?? '—' },
        { icon: 'mdi:check-circle-outline', color: 'tw-bg-emerald-500', label: 'Present', value: summary.present ?? '—' },
        { icon: 'mdi:circle-half-full', color: 'tw-bg-amber-500', label: 'Half day', value: summary.half_day ?? '—' },
        { icon: 'mdi:progress-clock', color: 'tw-bg-orange-500', label: 'Incomplete', value: summary.incomplete ?? '—' },
        { icon: 'mdi:account-off-outline', color: 'tw-bg-gray-400', label: 'Absent', value: summary.absent ?? '—' },
        { icon: 'mdi:login-variant', color: 'tw-bg-blue-500', label: 'On shift now', value: summary.currently_punched_in ?? '—' },
        { icon: 'mdi:timer-outline', color: 'tw-bg-indigo-500', label: 'Hours worked', value: summary.total_worked_display ?? '—' },
        {
            icon: 'mdi:gavel', color: 'tw-bg-rose-500', label: 'Pending approval',
            value: summary.punches_pending_approval ?? '—',
            onClick: canApprove ? openPendingQueue : undefined,
        },
    ];

    const dailyPg = adminDailyPagination || {};
    const punchesPg = adminPunchesPagination || {};
    const activePg = tab === 'daily' ? dailyPg : punchesPg;

    return (
        <>
            {/* Header */}
            <div className="tw-flex tw-items-start tw-justify-between tw-mb-4 tw-gap-3 tw-flex-wrap">
                <div>
                    <h1 className="tw-text-lg tw-font-bold tw-text-gray-800">Manage Attendance</h1>
                    <p className="tw-text-xs tw-text-gray-400 tw-mt-0.5">
                        Employee shift attendance — {formatDate(summary.attendance_date || range.to)}
                        {summary.attendance_enabled === false ? ' · attendance is disabled for this society' : ''}
                    </p>
                </div>
                <div className="tw-flex tw-gap-2">
                    {canManage && (
                        <button
                            type="button"
                            onClick={() => setManualOpen(true)}
                            className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-white tw-bg-blue-600 tw-rounded-md hover:tw-bg-blue-700"
                        >
                            <Icon icon="mdi:clock-plus-outline" /> Add missed punch
                        </button>
                    )}
                    {canExport && (
                        <button
                            type="button"
                            onClick={handleExport}
                            disabled={exporting}
                            className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-emerald-700 tw-border tw-border-emerald-200 tw-rounded-md hover:tw-bg-emerald-50 disabled:tw-opacity-50"
                        >
                            <Icon icon={exporting ? 'mdi:loading' : 'mdi:file-download-outline'} className={exporting ? 'tw-animate-spin' : ''} />
                            {exporting ? 'Preparing…' : 'Export Excel'}
                        </button>
                    )}
                </div>
            </div>

            {/* Summary tiles (for the range's end date) */}
            <div className="tw-grid tw-grid-cols-2 sm:tw-grid-cols-4 lg:tw-grid-cols-8 tw-gap-2.5 tw-mb-4">
                {tiles.map(tile => (
                    <div
                        key={tile.label}
                        onClick={tile.onClick}
                        className={`tw-bg-white tw-rounded-lg tw-border tw-border-gray-100 tw-p-2.5 tw-flex tw-items-center tw-gap-2 ${tile.onClick ? 'tw-cursor-pointer hover:tw-border-rose-200 hover:tw-shadow-sm' : ''}`}
                        title={tile.onClick ? 'Open the approval queue' : undefined}
                    >
                        <div className={`tw-w-8 tw-h-8 tw-rounded-lg tw-flex tw-items-center tw-justify-center tw-flex-shrink-0 ${tile.color}`}>
                            <Icon icon={tile.icon} className="tw-text-white tw-text-sm" />
                        </div>
                        <div className="tw-min-w-0">
                            <div className="tw-text-sm tw-font-bold tw-text-gray-800 tw-leading-tight tw-truncate">{tile.value}</div>
                            <div className="tw-text-[9px] tw-text-gray-500 tw-truncate">{tile.label}</div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Filters */}
            <div className="tw-bg-white tw-rounded-lg tw-border tw-border-gray-200 tw-p-4 tw-mb-4">
                <div className="tw-grid tw-grid-cols-2 md:tw-grid-cols-3 lg:tw-grid-cols-6 tw-gap-3">
                    <div className="tw-col-span-2 lg:tw-col-span-2">
                        <label className="tw-block tw-text-[11px] tw-font-medium tw-text-gray-500 tw-mb-1">Search</label>
                        <input
                            type="text"
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            placeholder="Name or employee code…"
                            className={selectClass}
                        />
                    </div>
                    <div>
                        <label className="tw-block tw-text-[11px] tw-font-medium tw-text-gray-500 tw-mb-1">Department</label>
                        <select value={department} onChange={(e) => { setDepartment(e.target.value); setPage(1); }} className={selectClass}>
                            <option value="">All</option>
                            {DEPARTMENTS.map(d => <option key={d} value={d}>{prettyEnum(d)}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="tw-block tw-text-[11px] tw-font-medium tw-text-gray-500 tw-mb-1">From</label>
                        <input type="date" value={range.from} onChange={(e) => { setRange(r => ({ ...r, from: e.target.value })); setPage(1); }} className={selectClass} />
                    </div>
                    <div>
                        <label className="tw-block tw-text-[11px] tw-font-medium tw-text-gray-500 tw-mb-1">To</label>
                        <input type="date" value={range.to} onChange={(e) => { setRange(r => ({ ...r, to: e.target.value })); setPage(1); }} className={selectClass} />
                    </div>
                    {tab === 'daily' ? (
                        <div>
                            <label className="tw-block tw-text-[11px] tw-font-medium tw-text-gray-500 tw-mb-1">Day status</label>
                            <select value={dayStatus} onChange={(e) => { setDayStatus(e.target.value); setPage(1); }} className={selectClass}>
                                <option value="">All</option>
                                {(attendanceEnums?.day_statuses || []).map(s => <option key={s} value={s}>{prettyEnum(s)}</option>)}
                            </select>
                        </div>
                    ) : (
                        <div>
                            <label className="tw-block tw-text-[11px] tw-font-medium tw-text-gray-500 tw-mb-1">Punch status</label>
                            <select value={punchStatus} onChange={(e) => { setPunchStatus(e.target.value); setPage(1); }} className={selectClass}>
                                <option value="">All</option>
                                {(attendanceEnums?.punch_statuses || []).map(s => <option key={s} value={s}>{prettyEnum(s)}</option>)}
                            </select>
                        </div>
                    )}
                </div>
                <div className="tw-flex tw-items-center tw-gap-4 tw-mt-3 tw-flex-wrap">
                    {tab === 'daily' && (
                        <label className="tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-text-gray-600 tw-cursor-pointer">
                            <input type="checkbox" checked={pendingOnly} onChange={(e) => { setPendingOnly(e.target.checked); setPage(1); }} />
                            Pending approval only
                        </label>
                    )}
                    {tab === 'punches' && (
                        <>
                            <label className="tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-text-gray-600 tw-cursor-pointer">
                                <input type="checkbox" checked={manualOnly} onChange={(e) => { setManualOnly(e.target.checked); setPage(1); }} />
                                Manual punches only
                            </label>
                            <div className="tw-w-32">
                                <select value={punchType} onChange={(e) => { setPunchType(e.target.value); setPage(1); }} className={selectClass}>
                                    <option value="">IN + OUT</option>
                                    {(attendanceEnums?.punch_types || []).map(t => <option key={t} value={t}>{t}</option>)}
                                </select>
                            </div>
                        </>
                    )}
                    <label className="tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-text-gray-600 tw-cursor-pointer">
                        <input type="checkbox" checked={outOfFenceOnly} onChange={(e) => { setOutOfFenceOnly(e.target.checked); setPage(1); }} />
                        Outside geofence only
                    </label>
                    <button
                        type="button"
                        onClick={() => {
                            setSearchInput(''); setDepartment(''); setDayStatus(''); setPendingOnly(false);
                            setOutOfFenceOnly(false); setPunchType(''); setPunchStatus(''); setManualOnly(false);
                            setEmployeeFocus(null); setRange({ from: todayInput(), to: todayInput() }); setPage(1);
                        }}
                        className="tw-ml-auto tw-flex tw-items-center tw-gap-1 tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-gray-500 tw-border tw-border-gray-200 tw-rounded-md hover:tw-bg-gray-50"
                    >
                        <Icon icon="mdi:refresh" className="tw-text-sm" /> Reset
                    </button>
                </div>
            </div>

            {/* Tabs + drill-down chip */}
            <div className="tw-flex tw-items-center tw-gap-2 tw-mb-3 tw-flex-wrap">
                {[
                    { key: 'daily', label: 'Daily grid', icon: 'mdi:calendar-multiselect' },
                    { key: 'punches', label: 'Punch log', icon: 'mdi:format-list-bulleted' },
                ].map(t => (
                    <button
                        key={t.key}
                        onClick={() => switchTab(t.key)}
                        className={`tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-rounded-md tw-border ${tab === t.key
                            ? 'tw-bg-blue-600 tw-text-white tw-border-blue-600'
                            : 'tw-bg-white tw-text-gray-600 tw-border-gray-200 hover:tw-bg-gray-50'}`}
                    >
                        <Icon icon={t.icon} /> {t.label}
                    </button>
                ))}
                {employeeFocus && tab === 'punches' && (
                    <span className="tw-flex tw-items-center tw-gap-1.5 tw-px-2.5 tw-py-1 tw-rounded-full tw-bg-blue-50 tw-text-blue-700 tw-text-xs">
                        <Icon icon="mdi:filter-outline" className="tw-text-sm" />
                        {employeeFocus.name} · {formatDate(employeeFocus.date)}
                        <button type="button" onClick={() => { setEmployeeFocus(null); setPage(1); }} className="hover:tw-text-red-600" title="Clear">
                            <Icon icon="mdi:close-circle" />
                        </button>
                    </span>
                )}
            </div>

            {/* Grids */}
            <div className="tw-bg-white tw-rounded-lg tw-border tw-border-gray-200 tw-overflow-hidden">
                <div className="tw-overflow-x-auto">
                    {tab === 'daily' ? (
                        <table className="tw-w-full tw-text-xs">
                            <thead>
                                <tr className="tw-bg-gray-50 tw-border-b tw-border-gray-200">
                                    {['Employee', 'Date', 'First in', 'Last out', 'Worked', 'Break', 'Punches', 'Status', ''].map((h, i) => (
                                        <th key={i} className="tw-px-3 tw-py-2.5 tw-text-left tw-text-[11px] tw-font-semibold tw-text-gray-600 tw-uppercase tw-tracking-wider tw-whitespace-nowrap">{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {adminDailyLoading ? (
                                    <tr><td colSpan={9} className="tw-text-center tw-py-12"><Icon icon="mdi:loading" className="tw-animate-spin tw-text-2xl tw-text-gray-300 tw-mx-auto" /></td></tr>
                                ) : (adminDailyRows || []).length === 0 ? (
                                    <tr><td colSpan={9} className="tw-text-center tw-py-12">
                                        <Icon icon="mdi:calendar-blank-outline" className="tw-text-4xl tw-text-gray-200 tw-mx-auto" />
                                        <p className="tw-text-xs tw-text-gray-400 tw-mt-2">No attendance records for these filters</p>
                                    </td></tr>
                                ) : (
                                    adminDailyRows.map(row => (
                                        <tr key={row.attendance_day_id} className="tw-border-b tw-border-gray-100 hover:tw-bg-gray-50/50 tw-cursor-pointer" onClick={() => openDayPunches(row)}>
                                            <td className="tw-px-3 tw-py-2">
                                                <div className="tw-font-medium tw-text-gray-800">{row.employee?.name || '—'}</div>
                                                <div className="tw-text-[10px] tw-text-gray-400">
                                                    {row.employee?.employee_code}{row.employee?.department ? ` · ${prettyEnum(row.employee.department)}` : ''}
                                                </div>
                                            </td>
                                            <td className="tw-px-3 tw-py-2 tw-whitespace-nowrap tw-text-gray-700">{formatDate(row.attendance_date)}</td>
                                            <td className="tw-px-3 tw-py-2 tw-whitespace-nowrap tw-text-gray-600">
                                                {formatTime(row.first_in_at)}
                                                {row.first_in_location_name && <div className="tw-text-[10px] tw-text-gray-400 tw-max-w-[140px] tw-truncate">{row.first_in_location_name}</div>}
                                                <DeviceInfo device={row.first_in_device} compact />
                                            </td>
                                            <td className="tw-px-3 tw-py-2 tw-whitespace-nowrap tw-text-gray-600">
                                                {formatTime(row.last_out_at)}
                                                {row.last_out_location_name && <div className="tw-text-[10px] tw-text-gray-400 tw-max-w-[140px] tw-truncate">{row.last_out_location_name}</div>}
                                                <DeviceInfo device={row.last_out_device} compact />
                                            </td>
                                            <td className="tw-px-3 tw-py-2 tw-whitespace-nowrap tw-font-medium tw-text-gray-800">
                                                {row.total_worked_display || '—'}
                                                {row.current_state === 'IN' && (
                                                    <span className="tw-ml-1.5 tw-inline-flex tw-items-center tw-gap-1 tw-text-[10px] tw-text-emerald-600">
                                                        <span className="tw-w-1.5 tw-h-1.5 tw-rounded-full tw-bg-emerald-500 tw-animate-pulse" /> on shift
                                                    </span>
                                                )}
                                            </td>
                                            <td className="tw-px-3 tw-py-2 tw-whitespace-nowrap tw-text-gray-600">{row.total_break_display || '—'}</td>
                                            <td className="tw-px-3 tw-py-2 tw-text-gray-600">
                                                {row.punch_count ?? 0}
                                                {row.out_of_fence_punch_count > 0 && (
                                                    <span className="tw-ml-1 tw-text-[10px] tw-text-amber-600" title={`${row.out_of_fence_punch_count} outside geofence`}>
                                                        ({row.out_of_fence_punch_count} out)
                                                    </span>
                                                )}
                                            </td>
                                            <td className="tw-px-3 tw-py-2 tw-whitespace-nowrap">
                                                <Badge value={row.day_status} styles={DAY_STATUS_STYLES} />
                                                {row.has_pending_approval && (
                                                    <span className="tw-ml-1.5 tw-inline-flex tw-px-1.5 tw-py-0.5 tw-rounded tw-text-[10px] tw-bg-blue-50 tw-text-blue-700">Pending</span>
                                                )}
                                            </td>
                                            <td className="tw-px-3 tw-py-2 tw-text-right">
                                                <Icon icon="mdi:chevron-right" className="tw-text-gray-300" />
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    ) : (
                        <table className="tw-w-full tw-text-xs">
                            <thead>
                                <tr className="tw-bg-gray-50 tw-border-b tw-border-gray-200">
                                    {['Employee', 'Punch', 'Location', 'Geofence', 'Photo', 'Source', 'Device', 'Status', 'Actions'].map((h) => (
                                        <th key={h} className="tw-px-3 tw-py-2.5 tw-text-left tw-text-[11px] tw-font-semibold tw-text-gray-600 tw-uppercase tw-tracking-wider tw-whitespace-nowrap">{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {adminPunchesLoading ? (
                                    <tr><td colSpan={9} className="tw-text-center tw-py-12"><Icon icon="mdi:loading" className="tw-animate-spin tw-text-2xl tw-text-gray-300 tw-mx-auto" /></td></tr>
                                ) : (adminPunches || []).length === 0 ? (
                                    <tr><td colSpan={9} className="tw-text-center tw-py-12">
                                        <Icon icon="mdi:gesture-tap" className="tw-text-4xl tw-text-gray-200 tw-mx-auto" />
                                        <p className="tw-text-xs tw-text-gray-400 tw-mt-2">No punches for these filters</p>
                                    </td></tr>
                                ) : (
                                    adminPunches.map(p => (
                                        <tr key={p.punch_id} className="tw-border-b tw-border-gray-100 hover:tw-bg-gray-50/50">
                                            <td className="tw-px-3 tw-py-2">
                                                <div className="tw-font-medium tw-text-gray-800">{p.employee?.name || '—'}</div>
                                                <div className="tw-text-[10px] tw-text-gray-400">
                                                    {p.employee?.employee_code}{p.employee?.department ? ` · ${prettyEnum(p.employee.department)}` : ''}
                                                </div>
                                            </td>
                                            <td className="tw-px-3 tw-py-2 tw-whitespace-nowrap">
                                                <span className={`tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-0.5 tw-rounded tw-text-[11px] tw-font-medium ${p.punch_type === 'IN' ? 'tw-bg-emerald-50 tw-text-emerald-700' : 'tw-bg-rose-50 tw-text-rose-700'}`}>
                                                    <Icon icon={p.punch_type === 'IN' ? 'mdi:login' : 'mdi:logout'} className="tw-text-xs" />
                                                    {p.punch_type}
                                                </span>
                                                <div className="tw-text-[10px] tw-text-gray-500 tw-mt-0.5">{formatDateTime(p.punch_at)}</div>
                                                {p.is_manual && (
                                                    <span className="tw-inline-flex tw-px-1.5 tw-py-0.5 tw-rounded tw-text-[10px] tw-bg-gray-100 tw-text-gray-500" title={p.manual_reason || ''}>Manual</span>
                                                )}
                                            </td>
                                            <td className="tw-px-3 tw-py-2 tw-text-gray-600 tw-max-w-[180px]">
                                                <div className="tw-truncate" title={p.location_address || ''}>
                                                    {p.location_name || (p.geocode_status === 'PENDING' ? 'Resolving…' : (p.latitude != null ? `${Number(p.latitude).toFixed(5)}, ${Number(p.longitude).toFixed(5)}` : '—'))}
                                                </div>
                                                {p.distance_meters != null && <div className="tw-text-[10px] tw-text-gray-400">{Math.round(p.distance_meters)} m from center</div>}
                                            </td>
                                            <td className="tw-px-3 tw-py-2">
                                                {p.is_within_geofence === true && <span className="tw-text-[11px] tw-px-2 tw-py-0.5 tw-rounded tw-bg-emerald-50 tw-text-emerald-700">Inside</span>}
                                                {p.is_within_geofence === false && <span className="tw-text-[11px] tw-px-2 tw-py-0.5 tw-rounded tw-bg-amber-50 tw-text-amber-700">Outside</span>}
                                                {p.is_within_geofence == null && <span className="tw-text-[11px] tw-text-gray-400">—</span>}
                                            </td>
                                            <td className="tw-px-3 tw-py-2">
                                                {p.selfie_view_url ? (
                                                    <img
                                                        src={resolveSelfieUrl(p.selfie_view_url)}
                                                        alt="Punch selfie"
                                                        className="tw-w-9 tw-h-9 tw-rounded-md tw-object-cover tw-border tw-border-gray-200 tw-cursor-pointer"
                                                        onClick={() => window.open(resolveSelfieUrl(p.selfie_view_url), '_blank', 'noopener')}
                                                        title="Open photo"
                                                    />
                                                ) : <span className="tw-text-[11px] tw-text-gray-300">—</span>}
                                            </td>
                                            <td className="tw-px-3 tw-py-2 tw-text-gray-500">{p.source || '—'}</td>
                                            <td className="tw-px-3 tw-py-2"><DeviceInfo device={p} /></td>
                                            <td className="tw-px-3 tw-py-2 tw-whitespace-nowrap"><Badge value={p.status} styles={PUNCH_STATUS_STYLES} /></td>
                                            <td className="tw-px-3 tw-py-2 tw-whitespace-nowrap">
                                                <div className="tw-flex tw-gap-1">
                                                    {canApprove && p.status === 'PENDING_APPROVAL' && (
                                                        <>
                                                            <button
                                                                type="button"
                                                                onClick={() => setDecision({ punch: p, mode: 'approve' })}
                                                                className="tw-px-2 tw-py-1 tw-rounded tw-text-[11px] tw-font-medium tw-text-emerald-700 tw-bg-emerald-50 hover:tw-bg-emerald-100"
                                                            >
                                                                Approve
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={() => setDecision({ punch: p, mode: 'reject' })}
                                                                className="tw-px-2 tw-py-1 tw-rounded tw-text-[11px] tw-font-medium tw-text-red-700 tw-bg-red-50 hover:tw-bg-red-100"
                                                            >
                                                                Reject
                                                            </button>
                                                        </>
                                                    )}
                                                    {canManage && p.status !== 'REJECTED' && (
                                                        <button
                                                            type="button"
                                                            onClick={() => setEditPunch(p)}
                                                            className="tw-px-2 tw-py-1 tw-rounded tw-text-[11px] tw-font-medium tw-text-gray-600 tw-bg-gray-100 hover:tw-bg-gray-200"
                                                            title="Correct time / direction / notes"
                                                        >
                                                            Edit
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {activePg.total > 0 && (
                <PaginationLimit
                    currentPage={page}
                    totalRecords={activePg.total || 0}
                    limit={limit}
                    handleLimitChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}
                    handlePageChange={(p) => setPage(p)}
                />
            )}

            {/* Modals */}
            <ManualPunchModal isOpen={manualOpen} onClose={() => setManualOpen(false)} onDone={refreshAll} />
            <EditPunchModal punch={editPunch} onClose={() => setEditPunch(null)} onDone={refreshAll} />
            <PunchDecisionModal
                punch={decision?.punch}
                mode={decision?.mode}
                onClose={() => setDecision(null)}
                onDone={refreshAll}
            />
        </>
    );
};

export default ManageAttendance;
